import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Check, Copy, ExternalLink, Share2 } from 'lucide-react'
import { Button, ButtonLink, Popover, TextField, copyText } from '@/components/play'

type CopyState = 'idle' | 'copied' | 'failed'

/**
 * Shares the event's public watch page from the workspace heading: a labelled button that opens the link in a
 * popover over the page, with Copy link and Open. While the public page is off it says so and leads to settings.
 */
export function ShareEvent({
  url,
  enabled,
  settingsTo,
  onCopy,
}: {
  url: string
  enabled: boolean
  settingsTo: string
  onCopy?: (copied: boolean) => void
}) {
  const [copy, setCopy] = useState<CopyState>('idle')
  // Opening lands on Copy link, so Enter copies and the link field shows its start.
  const copyButton = useRef<HTMLButtonElement>(null)
  const settingsLink = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (copy !== 'copied') return
    const timer = window.setTimeout(() => setCopy('idle'), 2000)
    return () => window.clearTimeout(timer)
  }, [copy])

  async function copyLink() {
    const copied = await copyText(url)
    setCopy(copied ? 'copied' : 'failed')
    onCopy?.(copied)
  }

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) setCopy('idle')
      }}
      trigger={<Button variant="secondary"><Share2 aria-hidden="true" />Share</Button>}
      title="Event page"
      description={enabled
        ? 'Anyone with the link can watch the featured camera, the map and the chat.'
        : 'Your public event page is off. Turn it on in settings to share it.'}
      align="end"
      initialFocus={enabled ? copyButton : settingsLink}
      footer={enabled ? (
        <>
          <ButtonLink variant="secondary" href={url} target="_blank" rel="noreferrer">
            Open
            <ExternalLink aria-hidden="true" />
          </ButtonLink>
          <Button ref={copyButton} onClick={() => void copyLink()}>
            {copy === 'copied' ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            {copy === 'copied' ? 'Copied' : 'Copy link'}
          </Button>
        </>
      ) : (
        <ButtonLink ref={settingsLink} to={settingsTo}>
          Set up public page
          <ArrowUpRight aria-hidden="true" />
        </ButtonLink>
      )}
    >
      {enabled && (
        <>
          <TextField
            label="Event page link"
            hideLabel
            value={url}
            readOnly
            error={copy === 'failed' ? 'Couldn’t copy. Select the link and copy it.' : undefined}
            onFocus={(event) => event.currentTarget.select()}
          />
          <span className="sr-only" role="status">{copy === 'copied' ? 'Link copied' : ''}</span>
        </>
      )}
    </Popover>
  )
}
